import { BindingError } from "../BindingError.js";
import { whereToFilter } from "@contember/client";
import levenshtein from "js-levenshtein";
import { QueryLanguage } from "../queryLanguage/QueryLanguage.js";
class TreeNodeEnvironmentFactory {
  static createEnvironmentForEntityListSubtree(environment, sugaredEntityList) {
    let entityList;
    let rootWhere;
    let expectedCardinality;
    if (sugaredEntityList.isCreating) {
      entityList = QueryLanguage.desugarUnconstrainedQualifiedEntityList(sugaredEntityList, environment);
      rootWhere = {};
      expectedCardinality = "zero";
    } else {
      entityList = QueryLanguage.desugarQualifiedEntityList(sugaredEntityList, environment);
      rootWhere = entityList.filter ?? {};
      expectedCardinality = "zero-to-many";
    }
    const entitySchema = this.resolveEntity(environment.getSchema(), entityList.entityName, "entity list");
    return environment.withSubTree({
      type: "subtree-entity-list",
      entity: entitySchema,
      expectedCardinality,
      filter: rootWhere
    });
  }
  static createEnvironmentForEntitySubtree(environment, sugaredEntityList) {
    let entity;
    let rootWhere;
    let expectedCardinality;
    if (sugaredEntityList.isCreating) {
      rootWhere = {};
      expectedCardinality = "zero";
      entity = QueryLanguage.desugarUnconstrainedQualifiedSingleEntity(sugaredEntityList, environment);
    } else {
      entity = QueryLanguage.desugarQualifiedSingleEntity(sugaredEntityList, environment, { missingSetOnCreate: "fill" });
      rootWhere = whereToFilter(entity.where);
      expectedCardinality = entity.setOnCreate ? "zero-or-one" : "one";
    }
    const entitySchema = this.resolveEntity(environment.getSchema(), entity.entityName, "entity");
    return environment.withSubTree({
      type: "subtree-entity",
      entity: entitySchema,
      expectedCardinality,
      filter: rootWhere
    });
  }
  static createEnvironmentForEntityList(environment, sugaredRelativeEntityList) {
    const relativeEntityList = QueryLanguage.desugarRelativeEntityList(sugaredRelativeEntityList, environment);
    const hasOneEnvironment = this.traverseHasOnePath(environment, relativeEntityList.hasOneRelationPath);
    const hasManyField = relativeEntityList.hasManyRelation.field;
    const field = this.assertHasManyRelation(hasOneEnvironment, hasManyField);
    const targetEntity = environment.getSchema().getEntity(field.targetEntity);
    return hasOneEnvironment.withSubTreeChild({
      type: "entity-list",
      field,
      entity: targetEntity
    });
  }
  static createEnvironmentForEntity(environment, sugaredRelativeSingleEntity) {
    const relativeSingleEntity = QueryLanguage.desugarRelativeSingleEntity(sugaredRelativeSingleEntity, environment);
    return this.traverseHasOnePath(environment, relativeSingleEntity.hasOneRelationPath);
  }
  static createEnvironmentForField(environment, sugaredRelativeSingleField) {
    const relativeSingleField = QueryLanguage.desugarRelativeSingleField(sugaredRelativeSingleField, environment);
    const hasOneEnvironment = this.traverseHasOnePath(environment, relativeSingleField.hasOneRelationPath);
    const field = this.resolveColumn(hasOneEnvironment, relativeSingleField.field);
    return hasOneEnvironment.withSubTreeChild({
      type: "column",
      entity: environment.getSubTreeNode().entity,
      field
    });
  }
  static traverseHasOnePath(environment, hasOneRelationPath) {
    for (const pathItem of hasOneRelationPath) {
      const field = this.resolveHasOneRelation(environment, pathItem.field, !!pathItem.reducedBy);
      const targetEntity = environment.getSchema().getEntity(field.targetEntity);
      environment = environment.withSubTreeChild({
        type: "entity",
        field,
        entity: targetEntity
      });
    }
    return environment;
  }
  static resolveHasOneRelation(environment, field, isReduced) {
    if (isReduced) {
      return this.resolveRelation(environment, field, "reduced has-one relation", ["ManyHasMany", "OneHasMany"]);
    }
    return this.resolveRelation(environment, field, "a has-one relation", ["ManyHasOne", "OneHasOne"]);
  }
  static assertHasManyRelation(environment, field) {
    return this.resolveRelation(environment, field, "a has-many relation", ["ManyHasMany", "OneHasMany"]);
  }
  static resolveRelation(environment, field, relationDescription, expectedRelationType) {
    return this.resolveField(environment, field, (field2) => {
      return field2.__typename === "_Relation" && expectedRelationType.includes(field2.type);
    }, relationDescription);
  }
  static resolveEntity(schema, entityName, type) {
    const entity = schema.getEntityOrUndefined(entityName);
    if (!entity) {
      const alternative = this.recommendAlternative(entityName, schema.getEntityNames());
      const didYouMean = alternative ? `Did you mean '${alternative}'?` : "";
      throw new BindingError(`Invalid ${type} sub tree: Entity '${entityName}' doesn't exist. ${didYouMean}`);
    }
    return entity;
  }
  static resolveColumn(environment, fieldName) {
    return this.resolveField(environment, fieldName, (field) => field.__typename === "_Column", "an ordinary field");
  }
  static resolveField(environment, fieldName, matcher, expectedDescription) {
    const treeLocation = environment.getSubTreeNode();
    const entity = treeLocation.entity;
    const field = entity.fields.get(fieldName);
    if (!field) {
      const alternative = this.recommendAlternative(
        fieldName,
        Array.from(entity.fields).filter(([, field2]) => matcher(field2)).map(([fieldName2]) => fieldName2)
      );
      const didYouMean = alternative ? `Did you mean '${alternative}'?` : "";
      throw new BindingError(`Field '${fieldName}' doesn't exist on ${this.describeLocation(environment)}. ${didYouMean}`);
    }
    if (!matcher(field)) {
      const actual = field.__typename === "_Column" ? "an ordinary field" : `a ${field.type} relation`;
      throw new BindingError(
        `Invalid field: the name '${field.name}' on ${this.describeLocation(environment)} refers to ${actual} but is being used as a ${expectedDescription}.`
      );
    }
    return field;
  }
  static describeLocation(environment) {
    const path = [];
    const loc = environment.getSubTreeNode();
    for (let env = environment; ; env = env.getParent()) {
      const node = env.getSubTreeNode();
      if (node.type === "subtree-entity" || node.type === "subtree-entity-list") {
        return `entity '${loc.entity.name}' in path '${[node.entity.name, ...path.reverse()].join(".")}'`;
      }
      path.push(node.field.name);
    }
  }
  static recommendAlternative(original, possibleAlternatives) {
    let bestAlternative = void 0;
    let bestAlternativeDistance = Number.MAX_SAFE_INTEGER;
    for (const alternative of possibleAlternatives) {
      const distance = levenshtein(original, alternative);
      if (distance < bestAlternativeDistance) {
        bestAlternative = alternative;
        bestAlternativeDistance = distance;
      }
    }
    return bestAlternative;
  }
}
export {
  TreeNodeEnvironmentFactory
};
//# sourceMappingURL=TreeNodeEnvironmentFactory.js.map
